import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { F8865SchdKService } from '../../services/f8865-schd-k.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-f8865-schd-k',
    templateUrl: './f8865-schd-k.component.html',
    styleUrls: ['./f8865-schd-k.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F8865SchdKComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  
  // Loading states
  showFormLoader = false;
  crudLoading = false;

  // Modal properties
  modalTitle = "8865 Schedule K - Partners' Distributive Share Items";
  
  // Entity data properties
  entityList: any[] = [];
  
  // Form data
  F8865SchdKWPDDetailForEntity: any[] = [];
  
  // Entity properties
  leId: any;
  cdr_no: any;
  leType: any;
  tax_year: any;
  scenario: any;
  jcd_key: any;
  leid: any;
  le_name: any;
  combination_key: any;
  issue_key: any;
  
  // Global settings
  globalService: any;
  userSettings: any;
  GENERAL_CONFIG: any;
  filterParams: any = {};
  rowData: any = {};

  constructor(
    private f8865SchdKService: F8865SchdKService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    try {
      let urls = safeJsonInput<any>(baseUrls, null, 'f8865-schd-k.parse1');
      if (Object.keys(urls).length > 0) {
        this.GENERAL_CONFIG = urls;
      }
    } catch (e) {
      /* input not yet stable */
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    try {
      let header = safeJsonInput<any>(headerData, null, 'f8865-schd-k.parse2');
      if (Object.keys(header).length > 0) {
        this.globalService = header.globalParams;
        this.userSettings = header.userSettingsData;
        this.GENERAL_CONFIG = header.generalConfigData;
        this.cdr.detectChanges();
      }
    } catch (e) {
      /* input not yet stable */
    }
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    try {
      let row = safeJsonInput<any>(rowData, null, 'f8865-schd-k.parse3');
      if (Object.keys(row).length > 0) {
        this.rowData = row;
        setTimeout(() => this.init(), 100);
      }
    } catch (e) {
      /* input not yet stable */
    }
  }

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    try {
      let params = safeJsonInput<any>(filterParams, null, 'f8865-schd-k.parse4');
      if (Object.keys(params).length > 0) {
        this.filterParams = params;
        setTimeout(() => this.init(), 100);
      }
    } catch (e) {
      /* input not yet stable */
    }
  }

  ngOnInit(): void {
    this.init();
  }

  init() {
    if (this.rowData && Object.keys(this.rowData).length > 0 && 
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && this.GENERAL_CONFIG.base_url) {
      this.setupEntityData();
      this.loadFormData();
    }
  }

  private setupEntityData() {
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;
    this.tax_year = this.rowData.TAX_YEAR;
    this.leid = this.rowData.LEID;
    this.le_name = this.rowData.LE_NAME;
    this.combination_key = this.rowData.COMBINATION_KEY;
    this.issue_key = this.rowData.ISSUE_KEY;
    
    this.scenario = this.filterParams.scenario;
    this.jcd_key = this.globalService?.jcd_key || this.filterParams.jcd_key || "250";

    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  loadFormData() {
    this.showFormLoader = true;
    
    const params = {
      action_code: '54528e',
      tax_year: this.tax_year,
      scenario: this.scenario,
      jcd_key: this.jcd_key,
      combination_key: this.combination_key
    };

    this.f8865SchdKService.load8865SchdKWPDForEntity(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.showFormLoader = false;
        
        if (response && response.jsonObject) {
          this.F8865SchdKWPDDetailForEntity = response.jsonObject;
          this.cdr.detectChanges();
        } else {
          this.F8865SchdKWPDDetailForEntity = [];
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.showFormLoader = false;
        this.gtwSnackbarService.openSnackBar('Error loading form data. Please try again.', 'gtw-snackbar--red');
        this.F8865SchdKWPDDetailForEntity = [];
      }
    );
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    const clob_settings = {
      tax_year: this.tax_year,
      scenario: this.scenario,
      jcd_key: this.jcd_key,
      combination_key: this.combination_key,
      sso_id: this.userSettings?.user?.sso_id,
      issue_key: this.issue_key
    };

    const clob_data: any[] = [];
    
    // Only save changed rows
    for (let x = 0; x < this.F8865SchdKWPDDetailForEntity.length; x++) {
      const row = this.F8865SchdKWPDDetailForEntity[x];
      if (row.IS_CHANGED === "Y") {
        clob_data.push(row);
      }
    }

    if (clob_data.length > 0) {
      this.f8865SchdKService.save8865SchdKWPDForEntity(
        this.GENERAL_CONFIG.base_url,
        clob_settings,
        clob_data,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;

          if (response.errorMessage && response.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('8865 Schedule K white paper details saved successfully.', 'gtw-snackbar--green');
            
            // CRITICAL: Emit 'refresh' string for AngularJS modal close
            this.requestGridUpdate.emit('refresh');
          }
        },
        (error: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        }
      );
    } else {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.gtwSnackbarService.openSnackBar('No changes to save.', 'gtw-snackbar--yellow');
    }
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  setChange(row: any) {
    row.IS_CHANGED = "Y";
  }

  get showSaveButton(): boolean {
    const utk = Number(
      this.userSettings?.user?.client?.user_type_key
      ?? this.userSettings?.user?.user_type_key
      ?? this.userSettings?.client?.user_type_key
      ?? this.userSettings?.user_type_key
    );
    if (!utk && utk !== 0) return true;
    return utk >= 2 && utk <= 5;
  }

  // TrackBy function for better performance
  trackByFn(index: number, item: any): any {
    return item ? item.LINE_NO || index : index;
  }
}
